import { io } from 'socket.io-client';
import { ADMIN_REALTIME_EVENTS } from '../realtime/realtimeEvents';
import { canViewWatchtower, hasPermission, PERMISSIONS, resolveUserPermissions } from '../security/acl';
import { log } from '../utils/log';

let socketInstance = null;
let realtimeConfig = { token: null, user: null };
let realtimeConfigKey = '';

function resolveRealtimeUrl() {
  const apiUrl =
    typeof import.meta !== 'undefined' && import.meta.env?.VITE_BFF_API_URL
      ? String(import.meta.env.VITE_BFF_API_URL).trim()
      : '';
  const envUrl =
    typeof import.meta !== 'undefined' && import.meta.env?.VITE_BFF_WS_URL
      ? String(import.meta.env.VITE_BFF_WS_URL).trim()
      : '';
  if (envUrl) return envUrl.replace(/\/$/, '');
  if (apiUrl) {
    try {
      return new URL(apiUrl).origin;
    } catch (_) {}
  }
  if (typeof window !== 'undefined') {
    const host = window.location.hostname || 'localhost';
    if (host === 'localhost' || host === '127.0.0.1') {
      const protocol = window.location.protocol || 'http:';
      return `${protocol}//${host}:4001`;
    }
    return window.location.origin;
  }
  return 'http://localhost:4001';
}

function buildRealtimeScopes(user) {
  const scopes = [];
  if (canViewWatchtower(user)) scopes.push('watchtower');
  if (hasPermission(user, PERMISSIONS.DASHBOARD_VIEW)) scopes.push('dashboard');
  return scopes;
}

function buildRealtimeConfigKey(user, token) {
  const scopes = buildRealtimeScopes(user).join(',');
  const perms = resolveUserPermissions(user).sort().join(',');
  return `${user?.id ?? ''}|${user?.role ?? ''}|${scopes}|${perms}|${token ?? ''}`;
}

/**
 * Reconnect Socket.IO when staff session or permissions change.
 */
export function configureAdminRealtime({ user, token } = {}) {
  const nextKey = buildRealtimeConfigKey(user, token);
  if (nextKey === realtimeConfigKey && socketInstance) {
    realtimeConfig = { user: user || null, token: token || null };
    return;
  }
  realtimeConfigKey = nextKey;
  realtimeConfig = { user: user || null, token: token || null };
  if (socketInstance) {
    socketInstance.disconnect();
    socketInstance = null;
  }
}

export function disconnectAdminRealtime() {
  realtimeConfigKey = '';
  configureAdminRealtime({ user: null, token: null });
}

export function getAdminRealtimeSocket() {
  const scopes = buildRealtimeScopes(realtimeConfig.user);
  if (!scopes.length) return null;

  if (socketInstance) return socketInstance;

  let token = realtimeConfig.token;
  try {
    if (!token) {
      token = sessionStorage.getItem('admin-staff-jwt');
      realtimeConfig.token = token;
    }
  } catch {
    /* ignore */
  }

  socketInstance = io(resolveRealtimeUrl(), {
    path: '/socket.io',
    transports: ['websocket', 'polling'],
    auth: token ? { token, scopes } : undefined,
    query: {
      role: 'admin',
      scopes: scopes.join(','),
      ...(token ? { token } : {}),
    },
  });

  socketInstance.on('connect', () => {
    log.info('[admin-realtime] connected', socketInstance?.id, scopes.join(','));
    socketInstance.emit(ADMIN_REALTIME_EVENTS.CLIENT_SUBSCRIBE, scopes);
  });
  socketInstance.on('connect_error', (err) => {
    log.error('[admin-realtime] connect_error', err?.message || err);
  });
  return socketInstance;
}
